import type {
  CrawlJobMonitorDTO,
  CrawlScheduleDTO,
} from '@liks/lac-svc-api/esm/models/index';
import type { I18nContextValue } from '../../types/i18nContext';
import { appendCrawlPhaseDurationDetailLines } from '../knowledge/crawlFormatters';
import { formatTimestamp } from '../../utils/formatUtils';

type Translate = I18nContextValue['translate'];

export type LacCrawlJobDetailLine = {
  text: string;
  indent?: boolean;
};

const CRAWL_PAGE_STATUSES = [
  'PENDING',
  'PROCESSING',
  'COMPLETED',
  'FAILED',
] as const;

const DOCUMENT_STATUSES = [
  'PENDING',
  'SCANNING',
  'UPLOADING',
  'EXTRACTING',
  'EMBEDDING',
  'READY',
  'FAILED',
] as const;

function countByStatus(
  counts:
    | ReadonlyArray<{ count?: number | null; status?: string | null }>
    | null
    | undefined,
): Map<string, number> {
  const map = new Map<string, number>();
  for (const entry of counts ?? []) {
    if (entry.status != null) {
      map.set(entry.status, entry.count ?? 0);
    }
  }
  return map;
}

function appendStatusCountSection(
  lines: LacCrawlJobDetailLine[],
  counts:
    | ReadonlyArray<{ count?: number | null; status?: string | null }>
    | null
    | undefined,
  options: {
    translate: Translate;
    headerKey: Parameters<Translate>[0];
    statusKeys: Record<string, Parameters<Translate>[0]>;
    statuses: readonly string[];
    doneStatuses: readonly string[];
    /** When false, only the done summary line is shown (no per-status rows). */
    includeStatusBreakdown?: boolean;
  },
) {
  if (counts == null) {
    return;
  }

  const {
    translate,
    headerKey,
    statusKeys,
    statuses,
    doneStatuses,
    includeStatusBreakdown = true,
  } = options;
  const byStatus = countByStatus(counts);

  let total = 0;
  let done = 0;
  for (const status of statuses) {
    const count = byStatus.get(status) ?? 0;
    total += count;
    if (doneStatuses.includes(status)) {
      done += count;
    }
  }

  const percent = total > 0 ? Math.round((done / total) * 100) : 0;
  lines.push({
    text: translate(headerKey, {
      count: String(done),
      percent: String(percent),
    }),
  });

  if (!includeStatusBreakdown) {
    return;
  }

  const hasAnyCount = statuses.some(
    (status) => (byStatus.get(status) ?? 0) > 0,
  );
  if (!hasAnyCount) {
    return;
  }

  for (const status of statuses) {
    const statusKey = statusKeys[status];
    if (!statusKey) {
      continue;
    }
    lines.push({
      text: translate('knowledge.crawlStatStatusCountLine', {
        status: translate(statusKey),
        count: String(byStatus.get(status) ?? 0),
      }),
      indent: true,
    });
  }
}

function formatScheduleInterval(
  schedule: CrawlScheduleDTO,
  translate: Translate,
): string {
  if (!schedule.scheduleInterval) {
    return translate('knowledge.notAvailable');
  }
  if (schedule.scheduleInterval === 'CUSTOM') {
    return translate('knowledge.crawlScheduleInterval.customEveryHours', {
      count: String(schedule.customIntervalHours ?? 24),
    });
  }
  return translate(
    `knowledge.crawlScheduleInterval.${schedule.scheduleInterval.toLowerCase()}`,
  );
}

function startedByDisplayName(job: CrawlJobMonitorDTO): string | null {
  const fullName = [job.startedByFirstName, job.startedByLastName]
    .map((part) => part?.trim())
    .filter(Boolean)
    .join(' ');
  if (fullName) {
    return fullName;
  }
  const username = job.startedByUsername?.trim();
  if (username) {
    return username;
  }
  const userRef = job.startedBy?.trim();
  return userRef || null;
}

export function resolveLacCrawlJobStartedBy(
  job: CrawlJobMonitorDTO,
): string | null {
  return startedByDisplayName(job);
}

/**
 * Detail lines for an expanded LAC crawl monitor row (fields beyond the summary).
 */
export function buildLacCrawlJobDetailLines(
  job: CrawlJobMonitorDTO,
  options: { translate: Translate; locale: string },
): LacCrawlJobDetailLine[] {
  const { translate, locale } = options;
  const lines: LacCrawlJobDetailLine[] = [];

  if (job.maxDepth != null) {
    lines.push({
      text: translate('knowledge.crawlMetaDepth', {
        value: String(job.maxDepth),
      }),
    });
  }

  if (job.maxPages != null) {
    lines.push({
      text: translate('knowledge.crawlMetaMaxPages', {
        value: String(job.maxPages),
      }),
    });
  }

  appendCrawlPhaseDurationDetailLines(
    lines,
    {
      mappingDuration: job.mappingDuration,
      crawlingDuration: job.crawlingDuration,
      embeddingDuration: job.embeddingDuration,
    },
    { translate, locale },
  );

  if (job.abandoned === true) {
    lines.push({ text: translate('knowledge.crawlDetailAbandoned') });
  }

  if (job.status === 'COMPLETED' && job.completedAt) {
    lines.push({
      text: translate('knowledge.crawlStatLastSuccessfulRun', {
        date: formatTimestamp(job.completedAt, locale),
      }),
    });
  }

  if (job.errorMessage?.trim()) {
    lines.push({
      text: translate('knowledge.crawlStatError', {
        message: job.errorMessage.trim(),
      }),
    });
  }

  if (job.hasSchedule === true) {
    lines.push({
      text: translate('lac.websiteCrawls.detail.hasScheduleYes'),
    });
  } else if (job.hasSchedule === false) {
    lines.push({
      text: translate('lac.websiteCrawls.detail.hasScheduleNo'),
    });
  }

  const schedule = job.schedule;
  if (schedule) {
    lines.push({
      text:
        schedule.active === true
          ? translate('knowledge.crawlScheduleMetaActive', {
              interval: formatScheduleInterval(schedule, translate),
            })
          : schedule.active === false
            ? translate('knowledge.crawlScheduleMetaPaused')
            : translate('lac.websiteCrawls.detail.scheduleInterval', {
                interval: formatScheduleInterval(schedule, translate),
              }),
      indent: true,
    });
    if (schedule.lastRunAt) {
      lines.push({
        text: translate('lac.websiteCrawls.detail.scheduleLastRun', {
          date: formatTimestamp(schedule.lastRunAt, locale),
        }),
        indent: true,
      });
    }
    if (schedule.nextRunAt) {
      lines.push({
        text: translate('lac.websiteCrawls.detail.scheduleNextRun', {
          date: formatTimestamp(schedule.nextRunAt, locale),
        }),
        indent: true,
      });
    }
  }

  appendStatusCountSection(lines, job.pageStatusCounts, {
    translate,
    headerKey: 'knowledge.crawlDetailPageStatus',
    statusKeys: {
      PENDING: 'knowledge.crawlPageStatus.PENDING',
      PROCESSING: 'knowledge.crawlPageStatus.PROCESSING',
      COMPLETED: 'knowledge.crawlPageStatus.COMPLETED',
      FAILED: 'knowledge.crawlPageStatus.FAILED',
    },
    statuses: CRAWL_PAGE_STATUSES,
    doneStatuses: ['COMPLETED', 'FAILED'],
  });

  appendStatusCountSection(lines, job.stagingDocumentStatusCounts, {
    translate,
    headerKey: 'lac.websiteCrawls.detail.stagingDocumentStatus',
    statusKeys: {
      PENDING: 'knowledge.documentStatus.PENDING',
      SCANNING: 'knowledge.documentStatus.SCANNING',
      UPLOADING: 'knowledge.documentStatus.UPLOADING',
      EXTRACTING: 'knowledge.documentStatus.EXTRACTING',
      EMBEDDING: 'knowledge.documentStatus.EMBEDDING',
      READY: 'knowledge.documentStatus.READY',
      FAILED: 'knowledge.documentStatus.FAILED',
    },
    statuses: DOCUMENT_STATUSES,
    doneStatuses: ['READY', 'FAILED'],
  });

  appendStatusCountSection(lines, job.publishedDocumentStatusCounts, {
    translate,
    headerKey: 'lac.websiteCrawls.detail.publishedDocumentStatus',
    statusKeys: {
      PENDING: 'knowledge.documentStatus.PENDING',
      SCANNING: 'knowledge.documentStatus.SCANNING',
      UPLOADING: 'knowledge.documentStatus.UPLOADING',
      EXTRACTING: 'knowledge.documentStatus.EXTRACTING',
      EMBEDDING: 'knowledge.documentStatus.EMBEDDING',
      READY: 'knowledge.documentStatus.READY',
      FAILED: 'knowledge.documentStatus.FAILED',
    },
    statuses: DOCUMENT_STATUSES,
    doneStatuses: ['READY', 'FAILED'],
    includeStatusBreakdown: false,
  });

  return lines;
}
